import React, { useCallback, useEffect, useState } from "react";
import { Check, FileText, MessageCircle, Phone, Plus, RefreshCw, UserX } from "lucide-react";
import { api, chargerPdf } from "../console/api";
import { Groupe, TitreGroupe, Vide } from "../espace/ui";
import CalendrierPrives from "../components/CalendrierPrives";
import { ContenuModification, useModification } from "../components/ModifierCoursPrive";
import { AjoutFait, ContenuAjout, useAjout } from "../components/AjouterCoursPrive";
import { Feuille } from "./Feuille";
import { fenetrePdf } from "./outils";
import { choixAbsence, duree as dureeTexte, heures, moinsDe, pourRienTexte, regles as reglesDe } from "../lib/reglesPrives";

/* Onglet « Privés » de l'app école (Jen, 30.10.2026 : « aussi sur l'application de Miller et
   Noémie admin ») : les annulations tardives à trancher (créditer le prof ou non), le
   calendrier de tous les profs (le mois, les jours disponibles en vert) et les cours réservés à
   venir, avec Écrire / Appeler. Un seul « Calendrier » (Jen, 31.10.2026) : Miller et Noémie
   règlent les disponibilités de tout le monde depuis leur téléphone — ce jour seulement ou
   toutes les semaines. Depuis le 01.11.2026 : « Annuler le cours… » (la case « La famille a
   annulé moins de 24 h avant : facturer 50 % ») et les cours passés avec « Absent·e — facturer ».
   Jen, 02.11.2026 : les cours réservés EN PREMIER (repliés au-delà de 3), puis le calendrier —
   en vert, les jours qui ont un cours réservé. Et « Modifier… » sur chaque cours à venir (« l'école
   doit pouvoir tout faire — modifier, annuler etc. ») : une feuille du bas pour changer la date et
   l'heure, la durée, le prof, la discipline, la note — la famille et le prof sont prévenus.
   Les seuils et pourcentages écrits ici (24 h, 50 %, moins d'1 h) sont ceux des Réglages › Cours
   privés (`d.regles`, Jen 02.11.2026). */
const tel = (n) => `tel:${String(n || "").replace(/[^\d+]/g, "")}`;
const sms = (n) => `sms:${String(n || "").replace(/[^\d+]/g, "")}`;
const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
// « Oui, il faut facturer aussi » (Jen, 01.11.2026) : un cours dont l'heure est passée, non
// annulé → « Absent·e — facturer » ; à moins de 24 h (ou passé), l'école qui annule pour la
// famille peut facturer 50 %. Les cours passés des 30 derniers jours restent dans la liste.
const debutDe = (r) => new Date(`${r.date}T${r.debut}:00`).getTime();
const commence = (r) => debutDe(r) <= Date.now();
const tardPourEcole = (r, R) => debutDe(r) - Date.now() < R.ecole_facturer_heures * 60 * 60 * 1000;
const JOURS_PASSES = 30;
const LIMITE = 3;               // sur téléphone, trois cours avant « Voir les N autres »
const jourCourt = (r) => `${String(r.quand || "").replace(/ à .*$/, "")} · ${r.debut}–${r.fin}`;
const etatFacture = (f) => (f.statut === "Annulée" ? "annulée" : f.statut === "Payée" ? "payée"
  : f.statut === "Partiellement payée" ? "payée en partie" : `${f.pourcentage || 50} % dus`);

/* Les feuilles du bas : « Modifier le cours », « Annuler le cours » et « Absent·e — facturer ». */
const BoutonsBas = ({ gauche, droite, onGauche, onDroite, occupe, danger = false, desactive = false, testId }) => (
  <div className="grid gap-2" style={{ gridTemplateColumns: "2fr 3fr" }}>
    <button type="button" className="rounded-full min-h-[50px] font-semibold border bg-white" style={{ borderColor: "var(--border)" }}
      disabled={occupe} onClick={onGauche} data-testid={`${testId}-non`}>{gauche}</button>
    <button type="button" className="rounded-full min-h-[50px] font-semibold text-white disabled:opacity-50 px-3"
      style={{ background: danger ? "#DC2626" : "var(--primary)" }} disabled={occupe || desactive} onClick={onDroite} data-testid={`${testId}-oui`}>
      {occupe ? "Un instant…" : droite}
    </button>
  </div>
);

function FeuilleAbsence({ r, R, regle, onFermer, onFait }) {
  const [pct, setPct] = useState(Number(R.absence_pourcentage));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const pourQui = r.pour === "enfant" && r.eleve_prenom ? `${r.eleve_prenom} ${r.eleve_nom || ""}`.trim() : `${r.prenom} ${r.nom}`;
  const facturer = async () => {
    setEnvoi(true); setErreur("");
    try { onFait(await api(`/reservations-privees/${r.id}/absent`, { method: "POST", body: JSON.stringify({ pourcentage: pct }) })); }
    catch (e) { setErreur(e.message); setEnvoi(false); }
  };
  return (
    <Feuille titre="Absent·e sans avoir annulé ?" sousTitre={`${r.discipline_label} · ${jourCourt(r)} · avec ${r.prof}`}
      onFermer={onFermer} occupe={envoi} testId="ecole-prives-absence"
      pied={<BoutonsBas gauche="Ne rien facturer" droite={`Facturer ${chf((Number(r.montant || 0) * pct) / 100)}`}
        onGauche={onFermer} onDroite={facturer} occupe={envoi} testId="ecole-prives-absence" />}>
      <p className="text-[14px] leading-snug"><strong>{pourQui}</strong> n'est pas venu·e et le cours n'avait pas été annulé. La facture part par e-mail (PDF joint, comment payer).{r.prof_salarie ? ` ${r.prof} touche quand même ${pourRienTexte(regle)} (déplacé·e pour rien).` : ""}</p>
      {choixAbsence(R).map(([p, titre, aide]) => (
        <label key={p} className="flex items-start gap-3 rounded-2xl border px-4 py-3" data-testid={`ecole-prives-absence-${p}`}
          style={pct === p ? { borderColor: "var(--primary)", background: "var(--primary-tint)" } : { borderColor: "var(--border)" }}>
          <input type="radio" name="ecole-absence" className="mt-1 w-4 h-4 accent-[var(--primary)]" checked={pct === p} onChange={() => setPct(p)} />
          <span className="flex-1">
            <span className="flex justify-between gap-3 text-[15px] font-semibold"><span>{titre}</span><span className="whitespace-nowrap">{chf((Number(r.montant || 0) * p) / 100)}</span></span>
            {aide && <span className="block text-[12.5px] text-[var(--muted)] mt-0.5">{aide}</span>}
          </span>
        </label>
      ))}
      {erreur && <p className="text-[13px]" style={{ color: "#B91C1C" }}>{erreur}</p>}
    </Feuille>
  );
}

/* « Modifier le cours » : le formulaire partagé avec la console (components/ModifierCoursPrive),
   dans une feuille du bas ; « Enregistrer » au pouce, grisé tant que rien ne change ou que le
   moment choisi est impossible. */
function FeuilleModification({ r, onFermer, onFait }) {
  const m = useModification(r, onFait);
  return (
    <Feuille titre="Modifier le cours" sousTitre={`${r.discipline_label} · ${r.eleve_prenom} ${r.eleve_nom} · ${jourCourt(r)} · avec ${r.prof}`}
      onFermer={onFermer} occupe={m.envoi} testId="ecole-prives-modif"
      pied={<BoutonsBas gauche="Garder" droite="Enregistrer" onGauche={onFermer} onDroite={m.enregistrer} occupe={m.envoi}
        desactive={!m.pret} testId="ecole-prives-modif" />}>
      <ContenuModification m={m} mobile />
    </Feuille>
  );
}

/* « Ajouter un cours » (retour de l'école, 03.11.2026) : le formulaire partagé avec la console
   (components/AjouterCoursPrive) — un cours réservé par téléphone ou un cours déjà donné. */
function FeuilleAjout({ depart, onFermer, onFait }) {
  const m = useAjout(onFait, depart);
  return (
    <Feuille titre="Ajouter un cours privé" sousTitre="Réservé par téléphone, au comptoir — ou déjà donné"
      onFermer={onFermer} occupe={m.envoi} testId="ecole-prives-ajout"
      pied={m.fait ? null : <BoutonsBas gauche="Annuler" droite={m.passe ? "Noter le cours" : "Ajouter"} onGauche={onFermer}
        onDroite={m.ajouter} occupe={m.envoi} desactive={!m.pret} testId="ecole-prives-ajout" />}>
      {m.fait ? <AjoutFait m={m} onFermer={onFermer} mobile /> : <ContenuAjout m={m} mobile />}
    </Feuille>
  );
}

function FeuilleAnnulation({ r, R, onFermer, onFait }) {
  const [texte, setTexte] = useState("");
  const [paye, setPaye] = useState(() => debutDe(r) - Date.now() < R.deplacement_minutes * 60 * 1000);
  const [facturer, setFacturer] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const tard = tardPourEcole(r, R);
  const annuler = async () => {
    setEnvoi(true); setErreur("");
    try {
      onFait(await api(`/reservations-privees/${r.id}/annuler`, { method: "POST",
        body: JSON.stringify({ message: texte, prof_paye: paye, facturer: facturer && tard }) }));
    } catch (e) { setErreur(e.message); setEnvoi(false); }
  };
  return (
    <Feuille titre="Annuler le cours ?" sousTitre={`${r.discipline_label} · ${r.eleve_prenom} ${r.eleve_nom} · ${jourCourt(r)} · avec ${r.prof}`}
      onFermer={onFermer} occupe={envoi} testId="ecole-prives-annulation"
      pied={<BoutonsBas gauche="Garder" droite="Annuler le cours" danger onGauche={onFermer} onDroite={annuler} occupe={envoi} testId="ecole-prives-annulation" />}>
      <textarea className="input-field text-[15px] w-full" rows={2} placeholder="Un mot pour la famille (facultatif)" value={texte} onChange={(e) => setTexte(e.target.value)} />
      <label className="flex items-start gap-3 text-[14px]">
        <input type="checkbox" className="mt-0.5 w-5 h-5 shrink-0" checked={paye} onChange={(e) => setPaye(e.target.checked)} />
        <span>Le prof s'est déplacé (annulé {moinsDe(dureeTexte(R.deplacement_minutes))} avant) : il est payé{R.salaire ? ` (${pourRienTexte(R.salaire)})` : ""}</span>
      </label>
      {tard && (
        <label className="flex items-start gap-3 text-[14px]">
          <input type="checkbox" className="mt-0.5 w-5 h-5 shrink-0" checked={facturer} onChange={(e) => setFacturer(e.target.checked)} data-testid="ecole-prives-facturer-tardive" />
          <span>La famille a annulé moins de {heures(R.ecole_facturer_heures)} avant : facturer {R.ecole_facturer_pourcentage} % ({chf((Number(r.montant || 0) * R.ecole_facturer_pourcentage) / 100)})</span>
        </label>
      )}
      {erreur && <p className="text-[13px]" style={{ color: "#B91C1C" }}>{erreur}</p>}
    </Feuille>
  );
}

export function PrivesMobile() {
  const [d, setD] = useState(null);
  const [cal, setCal] = useState(null);
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7));
  const [message, setMessage] = useState("");
  // Le « c'est enregistré » des disponibilités : une bulle en bas de l'écran, quelques
  // secondes (en haut de l'écran, on ne le voyait pas : la fenêtre se ferme sur le calendrier
  // ou la semaine type, plus bas).
  const [messageCal, setMessageCal] = useState("");
  useEffect(() => { if (!messageCal) return undefined; const t = setTimeout(() => setMessageCal(""), 3500); return () => clearTimeout(t); }, [messageCal]);
  const [erreur, setErreur] = useState("");
  const charger = useCallback(() => {
    api("/reservations-privees").then(setD).catch((e) => setErreur(e.message));
  }, []);
  // Les disponibilités d'un prof : ce jour seulement, toutes les semaines, ou retour à la
  // semaine type (plages = null) ; la liste et le calendrier se mettent à jour.
  const enregistrerDispos = async (pid, jour, plages, toutes) => {
    setMessage(""); setMessageCal("");
    if (plages === null) await api(`/reservations-privees/agenda/${pid}/jour/${jour}`, { method: "DELETE" });
    else if (toutes) await api(`/reservations-privees/agenda/${pid}/jour/${jour}?toutes=true`, { method: "PUT", body: JSON.stringify({ plages }) });
    else await api(`/reservations-privees/agenda/${pid}/jour/${jour}`, { method: "PUT", body: JSON.stringify({ plages }) });
    const qui = d?.profs.find((p) => p.prof_id === pid)?.prenom || "";
    setMessageCal(toutes ? `Semaine type de ${qui} enregistrée.` : `Disponibilités de ${qui} enregistrées.`);
    charger();
    api(`/reservations-privees/calendrier?du=${mois}-01&jours=42`).then(setCal).catch(() => {});
  };
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    api(`/reservations-privees/calendrier?du=${mois}-01&jours=42`).then(setCal).catch(() => setCal(null));
  }, [mois]);
  // Un seul envoi à la fois (série de tests, 02.11.2026 : un double toucher sur « Créditer le
  // prof » lui envoyait deux e-mails).
  const [decision, setDecision] = useState("");
  const decider = async (r, crediter) => {
    if (decision) return;
    setDecision(r.id); setErreur(""); setMessage("");
    try {
      const x = await api(`/reservations-privees/${r.id}/paie-prof`, { method: "POST", body: JSON.stringify({ crediter }) });
      setD(x); setMessage(x.message);
    } catch (e) { setErreur(e.message); }
    setDecision("");
  };
  // Annuler un cours, noter une absence (01.11.2026) : une feuille du bas, puis une bulle.
  const [annulation, setAnnulation] = useState(null);
  const [absence, setAbsence] = useState(null);
  const [modif, setModif] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [bulle, setBulle] = useState("");
  // Les cours réservés d'abord, repliés au-delà de LIMITE (Jen, 02.11.2026, comme la console).
  const [tous, setTous] = useState(false);
  useEffect(() => { if (!bulle) return undefined; const t = setTimeout(() => setBulle(""), bulle.length > 110 ? 9000 : 6000); return () => clearTimeout(t); }, [bulle]);
  const absenceFaite = (x) => { setAbsence(null); setErreur(""); setD(x); setBulle(x.message); };
  const modificationFaite = (x) => {
    setModif(null); setErreur(""); setD(x); setBulle(x.message);
    api(`/reservations-privees/calendrier?du=${mois}-01&jours=42`).then(setCal).catch(() => {});
  };
  const ajoutFait = (x) => {
    setErreur(""); setD(x); setBulle(x.message);
    api(`/reservations-privees/calendrier?du=${mois}-01&jours=42`).then(setCal).catch(() => {});
  };
  const annulationFaite = (x) => {
    setAnnulation(null); setErreur(""); setBulle(x.message); charger();
    api(`/reservations-privees/calendrier?du=${mois}-01&jours=42`).then(setCal).catch(() => {});
  };
  // La fenêtre s'ouvre au toucher et dit « Ouverture… » le temps que le PDF arrive (outils.js).
  const ouvrirFacture = (f) => fenetrePdf(() => chargerPdf(`/reservations-privees/factures/${encodeURIComponent(f.no_facture)}/pdf`),
    { titre: "Ouverture de la facture…" }).catch((e) => setErreur(e.message));
  if (erreur && !d) return <Vide>{erreur}</Vide>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  // Les règles de l'école (Réglages › Cours privés).
  // Les règles des Réglages, et celle du salaire du prof (école seulement : « il est payé (50 % du
  // salaire + le déplacement) »).
  const R = { ...reglesDe(d.regles), salaire: d.salaires?.regle };
  const aujourdhuiIds = new Set(d.a_venir.map((r) => r.id));
  const reserves = d.a_venir.filter((r) => r.statut !== "annulee" && !commence(r));
  const limite = new Date(Date.now() - JOURS_PASSES * 86400000).toLocaleDateString("sv-SE", { timeZone: "Europe/Zurich" });
  const passes = [...d.a_venir.filter((r) => commence(r)), ...d.passees]
    .filter((r) => r.statut !== "annulee" && r.date >= limite)
    .sort((a, b) => `${b.date}${b.debut}`.localeCompare(`${a.date}${a.debut}`));
  return (
    <div className="space-y-5" data-testid="ecole-prives">
      {message && <p className="text-[13px] px-1" style={{ color: "#15803D" }}>{message}</p>}
      {erreur && <p className="text-[13px] px-1" style={{ color: "#B91C1C" }}>{erreur}</p>}
      {(d.a_confirmer || []).length > 0 && (
        <section>
          <TitreGroupe>À confirmer · prof déplacé ?</TitreGroupe>
          <Groupe>
            {d.a_confirmer.map((r) => (
              <div key={r.id} className="px-4 py-3 space-y-2" data-testid="ecole-prives-a-confirmer">
                <div>
                  <div className="text-[15px] font-semibold">{r.prof} · {r.quand}</div>
                  <div className="text-[12px] text-[var(--muted)]">{r.discipline_label} · {r.eleve_prenom} {r.eleve_nom} · annulé {moinsDe(dureeTexte(R.deplacement_minutes))} avant</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button className="btn-primary text-sm justify-center" disabled={!!decision} onClick={() => decider(r, true)} data-testid="ecole-prives-crediter">Créditer le prof</button>
                  <button className="btn-ghost text-sm justify-center" disabled={!!decision} onClick={() => decider(r, false)} data-testid="ecole-prives-pas-crediter">Ne pas créditer</button>
                </div>
              </div>
            ))}
          </Groupe>
        </section>
      )}
      {/* Un cours réservé par téléphone, ou déjà donné (Jen, 03.11.2026 : retour de l'école). */}
      <button type="button" onClick={() => setAjout(true)} data-testid="ecole-prives-ajouter"
        className="w-full rounded-2xl min-h-[52px] font-semibold inline-flex items-center justify-center gap-2"
        style={{ background: "var(--primary)", color: "#04202A" }}>
        <Plus size={18} /> Ajouter un cours privé
      </button>
      <section>
        <TitreGroupe>Cours réservés · {reserves.length}</TitreGroupe>
        {!reserves.length ? <Vide>Aucun cours privé réservé.</Vide> : (
          <Groupe>
            {(tous ? reserves : reserves.slice(0, LIMITE)).map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid="ecole-prive">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-[var(--muted)]">{String(r.quand || "").replace(/ à .*$/, "")} · {r.debut}–{r.fin}</div>
                  <div className="text-[15px] font-semibold leading-snug">{r.discipline_label} · {r.eleve_prenom} {r.eleve_nom}</div>
                  <div className="text-[12px] text-[var(--muted)]">avec {r.prof} · {r.minutes} min · CHF {Number(r.montant || 0).toFixed(2)} sur place</div>
                  {(r.ancien_quand || r.ancien_minutes > 0 || r.repris_de) && (
                    <div className="text-[12px] font-medium" style={{ color: "#B45309" }} data-testid="ecole-prive-modifie">
                      {r.modifiee_le ? "Modifié par l'école" : "Déplacé par la famille"} · avant : {[r.ancien_quand, r.ancien_minutes > 0 ? `${r.ancien_minutes} min` : "", r.repris_de ? `avec ${r.repris_de}` : ""].filter(Boolean).join(", ")}
                    </div>
                  )}
                  <div className="flex gap-4 mt-1">
                    <button type="button" className="text-[13px] font-semibold min-h-[32px]" style={{ color: "#0E7490" }}
                      onClick={() => setModif(r)} data-testid="ecole-prive-modifier">Modifier…</button>
                    <button type="button" className="text-[13px] font-semibold min-h-[32px]" style={{ color: "#B91C1C" }}
                      onClick={() => setAnnulation(r)} data-testid="ecole-prive-annuler">Annuler le cours…</button>
                  </div>
                </div>
                {r.telephone && <a href={sms(r.telephone)} aria-label="Écrire" className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ border: "1px solid var(--border)" }}><MessageCircle size={16} /></a>}
                {r.telephone && <a href={tel(r.telephone)} aria-label="Appeler" className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ border: "1px solid var(--border)" }}><Phone size={16} /></a>}
              </div>
            ))}
            {reserves.length > LIMITE && (
              <button type="button" className="w-full py-3 text-[15px] font-medium" style={{ color: "#0E7490" }}
                onClick={() => setTous(!tous)} data-testid="ecole-prives-plus">
                {/* « Voir les 1 autres » au singulier (série de tests, 02.11.2026). */}
                {tous ? "Réduire" : reserves.length - LIMITE === 1 ? "Voir l'autre cours" : `Voir les ${reserves.length - LIMITE} autres`}
              </button>
            )}
          </Groupe>
        )}
      </section>
      <section>
        <TitreGroupe>Calendrier</TitreGroupe>
        {messageCal && (
          <div role="status" className="fixed inset-x-4 z-[70] mx-auto max-w-md rounded-2xl px-4 py-3 text-[14px] font-medium shadow-lg flex items-center gap-2"
            style={{ bottom: "calc(96px + env(safe-area-inset-bottom, 0px))", background: "#0F172A", color: "#fff" }} data-testid="ecole-prives-message">
            <Check size={16} className="shrink-0" /> {messageCal}
          </div>
        )}
        {cal ? <CalendrierPrives cal={cal} mois={mois} onMois={setMois} editables={d.profs.map((p) => p.prof_id)} onEnregistrer={enregistrerDispos}
          onAjouter={(x) => setAjout(x)} />
          : <Vide>Chargement…</Vide>}
      </section>
      {/* Les cours passés (30 jours) : la famille n'est pas venue sans annuler → « Absent·e —
          facturer » ; ensuite « Absent·e · Facture F-… · 50 % dus » (toucher : le PDF). */}
      <section>
        <TitreGroupe>Cours passés · {JOURS_PASSES} derniers jours</TitreGroupe>
        {!passes.length ? <Vide>Aucun cours passé ces {JOURS_PASSES} derniers jours.</Vide> : (
          <Groupe>
            {passes.map((r) => (
              <div key={r.id} className="px-4 py-3" data-testid="ecole-prive-passe">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] text-[var(--muted)]">{jourCourt(r)}</div>
                    <div className="text-[15px] font-semibold leading-snug">{r.discipline_label} · {r.eleve_prenom} {r.eleve_nom}</div>
                    <div className="text-[12px] text-[var(--muted)]">avec {r.prof} · {r.minutes} min · {chf(r.montant)}</div>
                  </div>
                  {r.telephone && <a href={tel(r.telephone)} aria-label="Appeler" className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ border: "1px solid var(--border)" }}><Phone size={16} /></a>}
                </div>
                {r.absent ? (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="ecole-prive-absent">
                    <span className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold" style={{ background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>Absent·e</span>
                    {r.facture && (
                      <button type="button" onClick={() => ouvrirFacture(r.facture)} className="inline-flex items-center gap-1 text-[13px] font-semibold underline underline-offset-2"
                        style={{ color: r.facture.statut === "Annulée" ? "var(--muted)" : r.facture.statut === "Payée" ? "#15803D" : "#B45309" }} data-testid="ecole-prive-facture">
                        <FileText size={13} /> Facture {r.facture.no_facture} · {etatFacture(r.facture)}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" className="rounded-full border px-3.5 min-h-[38px] text-[13.5px] font-semibold inline-flex items-center gap-1.5 bg-white"
                      style={{ borderColor: "var(--border)" }} onClick={() => setAbsence(r)} data-testid="ecole-prive-absent-facturer">
                      <UserX size={15} /> Absent·e — facturer
                    </button>
                    {aujourdhuiIds.has(r.id) && (
                      <button type="button" className="text-[13px] font-semibold px-2" style={{ color: "#B91C1C" }}
                        onClick={() => setAnnulation(r)} data-testid="ecole-prive-annuler">Annuler le cours…</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Groupe>
        )}
      </section>
      {bulle && (
        <div role="status" className="fixed inset-x-4 z-[70] mx-auto max-w-md rounded-2xl px-4 py-3 text-[14px] font-medium shadow-lg flex items-start gap-2"
          style={{ bottom: "calc(96px + env(safe-area-inset-bottom, 0px))", background: "#0F172A", color: "#fff" }} data-testid="ecole-prives-bulle">
          <Check size={16} className="shrink-0 mt-0.5" /> {bulle}
        </div>
      )}
      {absence && <FeuilleAbsence r={absence} R={R} regle={d.salaires?.regle} onFermer={() => setAbsence(null)} onFait={absenceFaite} />}
      {annulation && <FeuilleAnnulation r={annulation} R={R} onFermer={() => setAnnulation(null)} onFait={annulationFaite} />}
      {modif && <FeuilleModification r={modif} onFermer={() => setModif(null)} onFait={modificationFaite} />}
      {ajout && <FeuilleAjout depart={ajout === true ? null : ajout} onFermer={() => setAjout(false)} onFait={ajoutFait} />}
    </div>
  );
}
